/**
 * Fetches and caches the CSRF token issued by GET /api/csrf-token, for
 * attaching to mutating requests as the x-csrf-token header (double-submit
 * cookie pattern, see server/csrf.ts).
 */

let cachedTokenPromise: Promise<string> | null = null;

async function fetchCsrfToken(): Promise<string> {
    const response = await fetch("/api/csrf-token", { credentials: "include" });
    if (!response.ok) {
        throw new Error(`Failed to fetch CSRF token: ${response.status}`);
    }
    const data = await response.json();
    return data.csrfToken;
}

// On failure, clear the cache so the *next* call retries with a fresh
// fetch instead of forever reusing this same rejected promise -- a Promise
// is truthy even once rejected, so a naive `if (!cachedTokenPromise)` check
// would otherwise permanently wedge every caller after one transient error.
function trackAndReturn(promise: Promise<string>): Promise<string> {
    cachedTokenPromise = promise;
    promise.catch(() => {
        if (cachedTokenPromise === promise) {
            cachedTokenPromise = null;
        }
    });
    return promise;
}

export function getCsrfToken(): Promise<string> {
    if (!cachedTokenPromise) {
        return trackAndReturn(fetchCsrfToken());
    }
    return cachedTokenPromise;
}

/**
 * Forces a fresh token fetch. Needed right after login, since the server's
 * CSRF session identifier switches from the anonymous pre-login cookie to
 * the authenticated JWT cookie, invalidating any token issued before that.
 */
export function refreshCsrfToken(): Promise<string> {
    return trackAndReturn(fetchCsrfToken());
}
